import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getCustomer } from '../../../app/api';
import { formatCurrency } from '../../../app/currency';
import {
  EMPTY_QUOTE_META,
  buildQuoteModel,
  buildQuoteWhatsAppText,
  readQuoteMeta,
  validateQuoteModel,
} from './quoteExport';

/**
 * Collects the client-facing quote fields (payment terms, notes), checks what is missing
 * and exports: printable page in a new tab or text copied for WhatsApp.
 * loadOrder: persists pending edits and returns the fresh getOrder payload.
 * saveQuoteMeta: persists order.metadata.quote and returns the fresh order.
 */
export default function OrderQuoteExportDialog({ open, onClose, orderId, loadOrder, saveQuoteMeta, companyName, translate, notify }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [meta, setMeta] = useState(EMPTY_QUOTE_META);
  const [showMissing, setShowMissing] = useState(false);
  const loadRef = useRef(loadOrder);
  const notifyRef = useRef(notify);
  loadRef.current = loadOrder;
  notifyRef.current = notify;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setShowMissing(false);
    (async () => {
      try {
        const o = await loadRef.current();
        let c = null;
        if (o?.customer_id) {
          try {
            c = (await getCustomer(o.customer_id))?.data || null;
          } catch {
            c = null;
          }
        }
        if (cancelled) return;
        setOrder(o);
        setCustomer(c);
        setMeta(readQuoteMeta(o));
      } catch (e) {
        if (!cancelled) notifyRef.current(e.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const draftModel = order ? buildQuoteModel({ ...order, metadata: { ...(order.metadata || {}), quote: meta } }, customer) : null;
  const missing = draftModel ? validateQuoteModel(draftModel) : [];

  const persistIfChanged = useCallback(async () => {
    const saved = readQuoteMeta(order);
    const next = { payment_terms: meta.payment_terms.trim(), customer_notes: meta.customer_notes.trim() };
    if (saved.payment_terms === next.payment_terms && saved.customer_notes === next.customer_notes) return order;
    const fresh = await saveQuoteMeta(next);
    setOrder(fresh);
    return fresh;
  }, [order, meta, saveQuoteMeta]);

  const handlePrint = async () => {
    if (missing.length) {
      setShowMissing(true);
      return;
    }
    // Open synchronously so the browser does not treat it as a popup.
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      await persistIfChanged();
      const url = `/orders/${encodeURIComponent(orderId)}/quote`;
      if (win) win.location.href = url;
      else window.open(url, '_blank');
      onClose();
    } catch (e) {
      if (win) win.close();
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (missing.length) {
      setShowMissing(true);
      return;
    }
    setBusy(true);
    try {
      const fresh = await persistIfChanged();
      const text = buildQuoteWhatsAppText(buildQuoteModel(fresh, customer), companyName);
      await navigator.clipboard.writeText(text);
      notify(translate('orders.quote_copied'), 'success');
      onClose();
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('orders.quote_export_title')}</DialogTitle>
      <DialogContent>
        {loading || !draftModel ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {draftModel.number}
                {draftModel.customerName ? ` · ${draftModel.customerName}` : ''} · {draftModel.items.length}{' '}
                {translate('orders.quote_items_count')}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {formatCurrency(draftModel.total)}
              </Typography>
            </Box>
            <TextField
              label={translate('orders.quote_payment_terms')}
              placeholder={translate('orders.quote_payment_terms_placeholder')}
              value={meta.payment_terms}
              onChange={(e) => setMeta((m) => ({ ...m, payment_terms: e.target.value }))}
              required
              error={showMissing && !meta.payment_terms.trim()}
              size="small"
              fullWidth
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              label={translate('orders.quote_customer_notes')}
              placeholder={translate('orders.quote_customer_notes_placeholder')}
              helperText={translate('orders.quote_customer_notes_help')}
              value={meta.customer_notes}
              onChange={(e) => setMeta((m) => ({ ...m, customer_notes: e.target.value }))}
              multiline
              minRows={3}
              size="small"
              fullWidth
              inputProps={{ maxLength: 2000 }}
            />
            {missing.length > 0 ? (
              <Alert severity={showMissing ? 'error' : 'info'}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {translate('orders.quote_missing_title')}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {missing.map((k) => (
                    <li key={k}>
                      <Typography variant="body2">{translate(k)}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={busy} sx={{ mr: 'auto' }}>
          {translate('common.cancel')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<WhatsAppIcon />}
          onClick={handleCopy}
          disabled={loading || busy || !draftModel}
          data-testid="quote-copy-whatsapp"
        >
          {translate('orders.quote_copy_whatsapp')}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintOutlinedIcon />}
          onClick={handlePrint}
          disabled={loading || busy || !draftModel}
          data-testid="quote-open-print"
        >
          {translate('orders.quote_open_print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
